import { Announcement, BarChart } from '@utilities';

import { type ComparisonDimension, type ComparisonKeyFinding } from '../../utilities/comparison';
import { formatPercent, formatPercentagePoint } from '../../utilities/format';

import styles from '../../_index.module.scss';

interface ComparisonViewProps {
  dimensions: ComparisonDimension[];
  keyFindings: ComparisonKeyFinding[];
}

const noop = () => {};
const COMPARISON_BAR_COLOR = 'var(--color-icon-info)';
const LOW_N_BAR_COLOR = 'var(--color-border-subdued)';

const ComparisonChartLegend = ({ label }: { label: string }) => (
  <div
    aria-label={`${label} comparison chart legend`}
    className={styles['turf-simulator-chart-legend']}
  >
    <span>
      <span
        aria-hidden="true"
        className={styles['turf-simulator-chart-legend-swatch']}
        style={{ backgroundColor: COMPARISON_BAR_COLOR }}
      />
      Standard sample
    </span>
    <span>
      <span
        aria-hidden="true"
        className={styles['turf-simulator-chart-legend-swatch']}
        style={{ backgroundColor: LOW_N_BAR_COLOR }}
      />
      Low sample
    </span>
  </div>
);

const ComparisonView = ({ dimensions, keyFindings }: ComparisonViewProps) => {
  if (!dimensions.length && !keyFindings.length) return null;

  return (
    <div className={styles['turf-simulator-comparison']}>
      {keyFindings.length > 0 && (
        <Announcement
          className={styles['turf-simulator-key-findings']}
          closeButtonDataTestId="turf-simulator-key-findings-close-button"
          hasClose
          header="Key Findings"
          text={
            <ul>
              {keyFindings.map((finding) => (
                <li key={`${finding.dimensionId}-${finding.rowId}`}>
                  <strong>{finding.rowLabel}</strong> in {finding.dimensionLabel} reaches{' '}
                  <strong>{formatPercent(finding.reachPct)}</strong>,{' '}
                  {formatPercentagePoint(finding.overIndexPct)} above overall.
                </li>
              ))}
            </ul>
          }
          variant="info"
        />
      )}

      {dimensions.length > 0 && (
        <section className={styles['turf-simulator-comparison-blocks']}>
          <h3>Demographic Breakouts</h3>
          {dimensions.map((dimension) => (
            <div className={styles['turf-simulator-comparison-block']} key={dimension.id}>
              <BarChart
                chartTitle={dimension.label}
                hasLegend={false}
                maxHeight={Math.max(220, dimension.rows.length * 44)}
                onChartCallback={noop}
                plotOptions={{
                  series: {
                    dataLabels: {
                      format: '{point.y:.0f}%',
                    },
                  },
                }}
                prescriptText={null}
                series={[
                  {
                    data: dimension.rows.map((row) => ({
                      color: row.lowN ? LOW_N_BAR_COLOR : COMPARISON_BAR_COLOR,
                      name: row.label,
                      y: Math.round(row.reach.pct * 1000) / 10,
                    })),
                    name: 'Reach',
                  },
                ]}
                showExportMenu={false}
                xAxis={{
                  categories: dimension.rows.map(({ label }) => label),
                  title: { text: null },
                }}
                yAxis={{
                  labels: {
                    format: '{value}%',
                  },
                  max: 100,
                  min: 0,
                  plotLines: [
                    {
                      color: 'var(--color-border-active)',
                      dashStyle: 'Dash',
                      label: {
                        text: `Overall ${formatPercent(dimension.overallReach.pct)}`,
                      },
                      value: Math.round(dimension.overallReach.pct * 1000) / 10,
                      width: 2,
                    },
                  ],
                  title: { text: 'Reach %' },
                }}
              />
              <ComparisonChartLegend label={dimension.label} />
              <table
                aria-label={`${dimension.label} TURF comparison`}
                className={styles['turf-simulator-comparison-table']}
              >
                <thead>
                  <tr>
                    <th>Group</th>
                    <th>Reach %</th>
                    <th>Reach Count</th>
                    <th>n</th>
                    <th>Index vs Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {dimension.rows.map((row) => (
                    <tr
                      className={row.lowN ? styles['is-low-n'] : undefined}
                      key={`${dimension.id}-${row.id}`}
                    >
                      <td>
                        {row.label}
                        {row.lowN && (
                          <span className={styles['turf-simulator-table-meta']}>Low n</span>
                        )}
                      </td>
                      <td>{formatPercent(row.reach.pct)}</td>
                      <td>{row.reach.hit}</td>
                      <td>{row.n}</td>
                      <td>{formatPercentagePoint(row.overIndexPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>
      )}
    </div>
  );
};

export default ComparisonView;
